import type {
	LedgerCounts,
	LedgerMatchSignal,
	LedgerStatus,
	LedgerSyncOutcome,
	LedgerTransaction,
	Reconciliation,
} from "@ravnar/api-contract";

import type { Tone } from "../components/ui/tone";

/**
 * The ledger's statuses, the reasons for no document and their labels come from the API contract, and so does what a
 * document is filed as, in a word or two.
 */
export {
	filedAsLabel,
	LEDGER_STATUS_LABELS,
	LEDGER_STATUSES,
	NO_DOCUMENT_NOTE_MAX_LENGTH,
	NO_DOCUMENT_REASON_LABELS,
	NO_DOCUMENT_REASONS,
	type LedgerStatus,
} from "@ravnar/api-contract";

/**
 * The tone of each status's pill: what is left to do in the colours that ask for a look — a document to find, a
 * suggestion to confirm — the trail complete in green, or quiet when nothing was needed.
 */
export const LEDGER_STATUS_TONES: Readonly<Record<LedgerStatus, Tone>> = {
	"needs-document": "warn",
	"to-confirm": "info",
	matched: "ok",
	"no-document": "neutral",
};

/**
 * How much of the year's trail is complete, in whole percent: the transactions matched to a document or needing none,
 * rounded down, so it reads 100 only when nothing is left. Undefined for a year with no transaction yet.
 */
export function trailComplete({ total, counts }: Pick<Reconciliation, "total" | "counts">): number | undefined {
	// Multiplied before it is divided, which is exact: 29 of 100 divided first is 28.999…, and would read 28.
	return total === 0 ? undefined : Math.floor(((counts.matched + counts["no-document"]) * 100) / total);
}

/** How many transactions of the year are left to resolve: those that need a document, and those to confirm. */
export function unresolvedCount(counts: LedgerCounts): number {
	return counts["needs-document"] + counts["to-confirm"];
}

/** What the last attempt to read the bank came to, when it did not read it: why the ledger may be behind. */
export const LEDGER_SYNC_PROBLEMS: Readonly<Record<Exclude<LedgerSyncOutcome, "synced">, string>> = {
	"not-connected": "No bank is connected",
	reconnect: "Revolut needs reconnecting",
	failed: "Revolut could not be read",
};

/** Each way a document agrees with a transaction, as a suggestion says it. */
export const LEDGER_MATCH_SIGNAL_LABELS: Readonly<Record<LedgerMatchSignal, string>> = {
	amount: "amount",
	dates: "dates",
	names: "party",
	number: "number",
};

/** "Agrees on amount, dates and party": every way a suggested document agrees with the transaction. */
export function agreementLine(signals: readonly LedgerMatchSignal[]): string {
	const words = signals.map(signal => LEDGER_MATCH_SIGNAL_LABELS[signal]);
	const last = words.at(-1);
	return last === undefined || words.length < 2
		? `Agrees on ${words.join("")}`
		: `Agrees on ${words.slice(0, -1).join(", ")} and ${last}`;
}

/** What a transaction is called at the head of its drawer: money that left the account, or came into it. */
export function transactionKind({ amount }: Pick<LedgerTransaction, "amount">): string {
	return amount.amount.startsWith("-") ? "Outgoing payment" : "Incoming payment";
}
